import { forwardRef, useEffect, useMemo, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useParams } from "react-router";
import { c34Fields } from "../../FeuConstants";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { MDBCol, MDBRow } from "mdbreact";
import { Button, Label, TextArea } from "abs-ui-components";
import AutoSave from "../../../../../common/AutoSave/AutoSave";
import { AUTOSAVE_SUCCESS_MESSAGE } from "../../../Mrv/Constants";

const FeuC34SeaBerthTimeProcedures = forwardRef(({ c34SeaBerthTimeProcedures, refetchMonitoringData,vesselMstId }, ref) => {
    const { control, getValues, reset, formState } = useFormContext();
    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);
    const savedValuesRef = useRef({});
    const { dirtyFields } = formState;
    const isC34Dirty = !!(dirtyFields?.c34Fields && Object.keys(dirtyFields.c34Fields).length > 0);

    useEffect(() => {
        if (c34SeaBerthTimeProcedures) {
            reset(prev => ({ ...prev, c34Fields: c34SeaBerthTimeProcedures }));
        }
    }, [c34SeaBerthTimeProcedures, reset]);


    const c12Fields = useMemo(() => {
        // Map your frontend form field names to backend API keys
        const feildMapping = {
            procedureReference: "referenceToProcedure",
            procedureVersion: "versionOfProcedure",
            procedureDescription: "descriptionOfProcedure",
            responsiblePersonOrPosition: "responsiblePersonOrPosition",
            formulaeAndDataSources: "formulaeAndDataSources",
            recordLocation: "recordLocation",
            itSystemName: "itSystemName",
        };

        return c34Fields.reduce((acc, field) => {
            if (field.name === "dataSources") return acc;
            const mappedFeild = feildMapping[field.name] || field.name;

            // Override label if it's the description field
            const label =
                field.name === "procedureDescription"
                    ? "Description of procedure (a brief description of the procedure can be provided if already existing outside the monitoring plan)"
                    : field.label;

            // Push the transformed field into accumulator
            acc.push({
                ...field,
                name: mappedFeild,
                label,
            });

            return acc;
        }, []);
    }, []);

    AutoSave(ref, [], async () => {
        const currentValues = getValues("c34Fields");
        const isChanged = Object.keys(currentValues || {}).some(
            key => currentValues[key] !== savedValuesRef.current?.[key]
        );
        if (!isC34Dirty || !isChanged) return;
        await handleSave(true);
    });

    const handleSave = async (isAuto = false) => {
        const formValues = getValues("c34Fields");
        const payload = {
            ...c12Fields.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] || "";
                return acc;
            }, {}),
        };
        if (formValues.id) {
            payload.id = formValues.id;
        }

        const isUpdate = !!formValues.id;
        try {
            const C34FuelBunkered = await callApi({
                method: "POST",
                url: API_ROUTES.C34(vesselMstId),
                data: payload,
            });
            if (C34FuelBunkered?.isSuccess) {
                emitToasterMsg("success", "Table C.3.4", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, c1: formValues }));
                savedValuesRef.current = { ...formValues };
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                // emitToasterMsg("success", "Table C34", isUpdate ? "Updated successfully" : "Created successfully");
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table C.3.4", C34FuelBunkered?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.3.4", error.errorMessage);
        }
    };

    return (
        <div className="b7-section-form mt-4">
            <MDBRow className="mb-2">
                <MDBCol md="4">
                    <h6 className="titles-font">Title of procedure</h6>
                </MDBCol>
                <MDBCol md="4">
                    <h6 className="titles-font">Determining and recording the time spent at sea from berth of port of departure to berth of the port of arrival, and time spent at berth (quayside and anchorage)</h6>
                </MDBCol>
            </MDBRow>
            {c12Fields.map(({ label, name, maxLength }, index) => (
                <MDBRow className="align-items-center mb-3" key={index}>
                    <MDBCol md="4">
                        <Label name={label} bold />
                    </MDBCol>
                    <MDBCol md="4">
                        <Controller
                            name={`c34Fields.${name}`}
                            control={control}
                            render={({ field }) => (
                                <TextArea
                                    id={`c12_${name}`}
                                    className="textarea-resize"
                                    value={field?.value || ""}
                                    onChange={field.onChange}
                                    placeholder=""
                                    maxLength={maxLength}
                                    rows={1}
                                />
                            )}
                        />
                    </MDBCol>
                </MDBRow>
            ))}

            <div className="text-center">
                <Button id="c12_save_btn"
                    disabled={!isC34Dirty}
                    onClick={async () => {
                        manualSaveTriggered.current = true;
                        await handleSave(false);
                    }}>
                    Save
                </Button>
            </div>
        </div>
    );
})

export default FeuC34SeaBerthTimeProcedures;